import { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import {
  Flag,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  User,
  Package,
  AlertTriangle,
  ShieldAlert,
  Ban,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../lib/toast';

const REPORT_REASONS = {
  prohibited_item: { label: 'Prohibited Item', color: 'bg-red-100 text-red-700' },
  misleading_description: { label: 'Misleading Description', color: 'bg-orange-100 text-orange-700' },
  suspected_scam: { label: 'Suspected Scam', color: 'bg-red-100 text-red-700' },
  inappropriate_content: { label: 'Inappropriate Content', color: 'bg-purple-100 text-purple-700' },
  duplicate_listing: { label: 'Duplicate Listing', color: 'bg-yellow-100 text-yellow-700' },
  harassment: { label: 'Harassment', color: 'bg-red-100 text-red-700' },
  impersonation: { label: 'Impersonation', color: 'bg-indigo-100 text-indigo-700' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-600' },
  spam: { label: 'Spam', color: 'bg-yellow-100 text-yellow-700' },
  scam: { label: 'Scam', color: 'bg-red-100 text-red-700' },
  inappropriate: { label: 'Inappropriate', color: 'bg-orange-100 text-orange-700' },
  fake_listing: { label: 'Fake Listing', color: 'bg-purple-100 text-purple-700' },
};

const STATUS_BADGES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  reviewed: { label: 'Reviewed', color: 'bg-blue-100 text-blue-700', icon: Eye },
  actioned: { label: 'Actioned', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  dismissed: { label: 'Dismissed', color: 'bg-gray-100 text-gray-600', icon: XCircle },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700', icon: Eye },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

const formatDate = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export default function AdminCommunityReportsPage() {
  const { token, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expandedReport, setExpandedReport] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [token, statusFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ reporter_type: 'community', limit: '50' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/reports/admin/all?${params}`);
      setReports(res.data || []);
    } catch (err) {
      console.error('Failed to load reports:', err);
      toast.error('Failed to load reports');
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/reports/admin/stats');
      setStats(res.data);
    } catch (err) {
      console.log('Stats not available');
    }
  };

  const handleAction = async (reportId, action) => {
    setActionLoading(reportId);
    try {
      await api.post(`/reports/admin/${reportId}/action?action=${action}`, {
        admin_notes: adminNotes || null
      });
      const actionLabels = { dismiss: 'dismissed', warn: 'warned user', hide: 'hidden listing', ban: 'banned listing' };
      toast.success(`Report ${actionLabels[action] || 'updated'}`);
      setAdminNotes('');
      setExpandedReport(null);
      fetchReports();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to action report');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_BADGES[status] || STATUS_BADGES.pending;
    const Icon = config.icon;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getReasonBadge = (reason) => {
    const config = REPORT_REASONS[reason] || REPORT_REASONS.other;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getItemTypeIcon = (itemType) => {
    if (itemType === 'wtb') return <ShieldAlert className="w-5 h-5 text-blue-600" />;
    if (itemType === 'user') return <User className="w-5 h-5 text-red-600" />;
    return <Package className="w-5 h-5 text-amber-600" />;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Flag className="w-6 h-6 text-red-500" />
              Community Reports
            </h1>
            <p className="text-gray-500">User-to-user reports on listings, messages, and users</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { fetchReports(); fetchStats(); }} className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-yellow-600 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Pending</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-blue-600 mb-1">
                <Eye className="w-4 h-4" />
                <span className="text-sm font-medium">Reviewed</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.reviewed || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Actioned</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.actioned || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 mb-1">
                <XCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Dismissed</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.dismissed || 0}</p>
            </div>
          </div>
        )}

        {/* Status Filter */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['pending', 'reviewed', 'actioned', 'dismissed', ''].map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(s)}
              className={statusFilter === s ? 'bg-[#1E3A5F]' : ''}
            >
              {s ? (STATUS_BADGES[s]?.label || s) : 'All'}
              {stats && s && <span className="ml-1 text-xs opacity-70">({stats[s] || 0})</span>}
            </Button>
          ))}
        </div>

        {/* Reports List */}
        <div className="space-y-4">
          {reports.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <Flag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No reports</h3>
              <p className="text-gray-500">
                {statusFilter ? `No ${statusFilter} community reports found` : 'No community reports have been submitted'}
              </p>
            </div>
          ) : (
            reports.map((report) => (
              <div key={report.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                {/* Report Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        report.item_type === 'user' ? 'bg-red-100' : report.item_type === 'wtb' ? 'bg-blue-100' : 'bg-amber-100'
                      }`}>
                        {getItemTypeIcon(report.item_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-gray-900">
                            {report.item_title || 'Unknown'}
                          </p>
                          {getReasonBadge(report.reason)}
                        </div>
                        <p className="text-xs text-gray-500">
                          {report.item_type === 'wtb' ? 'Want Ad' : report.item_type === 'user' ? 'User Report' : 'Item'} {' \u2022 '}
                          Reported by {report.reporter_username || 'Anonymous'} {' \u2022 '}
                          {formatDate(report.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(report.status)}
                      {expandedReport === report.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedReport === report.id && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          {report.item_type === 'user' ? 'Reported User' : 'Reported Listing'}
                        </h4>
                        <div className="bg-white p-3 rounded-lg border border-gray-200">
                          <p className="font-medium text-gray-900">{report.item_title || 'Unknown'}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Type: {report.item_type === 'wtb' ? 'Want Ad' : report.item_type === 'user' ? 'User' : 'Item for Sale'}
                          </p>
                          <p className="text-xs text-gray-400 font-mono mt-1">ID: {report.item_id}</p>
                          {report.item_type !== 'user' && (
                            <a href={`/${report.item_type === 'wtb' ? 'wtb' : 'catalog'}/${report.item_id}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">
                              <ExternalLink className="w-3 h-3" /> View Listing
                            </a>
                          )}
                          {report.item_type === 'user' && (
                            <a href={`/profile/${report.item_id}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">
                              <ExternalLink className="w-3 h-3" /> View Profile
                            </a>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Reporter</h4>
                        <div className="bg-white p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <p className="text-gray-900">{report.reporter_username || 'Anonymous'}</p>
                          </div>
                          <p className="text-xs text-gray-400 font-mono mt-1">ID: {report.reporter_id}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Reason: {REPORT_REASONS[report.reason]?.label || report.reason}
                      </h4>
                      {report.details ? (
                        <div className="bg-white p-3 rounded-lg border border-gray-200">
                          <p className="text-gray-600 text-sm whitespace-pre-wrap">{report.details}</p>
                        </div>
                      ) : (
                        <p className="text-gray-400 text-sm italic">No additional details provided</p>
                      )}
                    </div>

                    {(report.status === 'pending' || report.status === 'reviewed') && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Admin Notes</h4>
                        <Textarea
                          placeholder="Add notes about your decision (optional)..."
                          value={expandedReport === report.id ? adminNotes : ''}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          className="mb-3 bg-white"
                          rows={2}
                        />
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => handleAction(report.id, 'dismiss')} disabled={actionLoading === report.id} className="border-gray-300">
                            {actionLoading === report.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" /> Dismiss</>}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleAction(report.id, 'warn')} disabled={actionLoading === report.id} className="border-yellow-300 text-yellow-700 hover:bg-yellow-50">
                            <AlertTriangle className="w-4 h-4 mr-1" /> {report.item_type === 'user' ? 'Warn User' : 'Warn Owner'}
                          </Button>
                          {report.item_type !== 'user' && (
                            <Button size="sm" variant="outline" onClick={() => handleAction(report.id, 'hide')} disabled={actionLoading === report.id} className="border-orange-300 text-orange-700 hover:bg-orange-50">
                              <Eye className="w-4 h-4 mr-1" /> Hide Listing
                            </Button>
                          )}
                          <Button size="sm" onClick={() => handleAction(report.id, 'ban')} disabled={actionLoading === report.id} className="bg-red-600 hover:bg-red-700 text-white">
                            <Ban className="w-4 h-4 mr-1" /> {report.item_type === 'user' ? 'Ban User' : 'Ban Listing'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {(report.status === 'actioned' || report.status === 'dismissed') && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="bg-white p-3 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Action taken:</span> {report.action_taken || report.resolution || 'N/A'}
                          </p>
                          {report.admin_notes && (
                            <p className="text-sm text-gray-500 mt-1"><span className="font-medium">Notes:</span> {report.admin_notes}</p>
                          )}
                          {report.actioned_at && (
                            <p className="text-xs text-gray-400 mt-2">Processed: {formatDate(report.actioned_at)}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
